import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ArrowPathIcon, BanknotesIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { downloadPayslipPdf, fetchPayslips, queuePayslipSync } from "../../../api/payslips";
import { PeriodSelector } from "../../../components/dashboard/hours/period_selector";
import { EmployeeSelector } from "../../../components/utils/moduleSelectors/employeeSelector";
import { useRoleViewOverride } from "../../../contexts/RoleViewOverrideContext";

const today = new Date();
const initialPeriod = {
  periodType: "month",
  from: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10),
  to: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10),
};

export const PayslipList = ({ mode = "mine" }) => {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ data: [], isLoading: true, error: null, canManage: false });
  const [openingId, setOpeningId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [period, setPeriod] = useState(initialPeriod);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const isAdminMode = mode === "admin";
  const isStatsMode = mode === "stats";

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language || "no", { year: "numeric", month: "2-digit", day: "2-digit" }),
    [i18n.language]
  );

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language || "no", { style: "currency", currency: "NOK" }),
    [i18n.language]
  );

  const roleViewCtx = useRoleViewOverride();

  const isPayrollAdmin = useSelector((state) =>
    (state.session.currentUser?.roles || []).some((r) => r.name === "payslips_admin")
  );

  const loadPayslips = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const params = {};
      if (isAdminMode) {
        params.company_wide = true;
        params.from = period.from;
        params.to = period.to;
        if (selectedEmployee?.id) {
          params.user_id = selectedEmployee.id;
        }
      } else if (isStatsMode && isPayrollAdmin) {
        params.company_wide = true;
      }
      const response = await fetchPayslips(params);
      setState({
        data: response.data?.data || [],
        canManage: response.data?.meta?.can_manage || false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setState({ data: [], canManage: false, isLoading: false, error });
    }
  }, [isAdminMode, isStatsMode, isPayrollAdmin, period.from, period.to, selectedEmployee?.id]);

  useEffect(() => {
    loadPayslips();
  }, [loadPayslips]);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return currencyFormatter.format(Number(value));
  };

  const openPayslip = async (payslip) => {
    setOpeningId(payslip.id);
    try {
      const response = await downloadPayslipPdf(payslip.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } finally {
      setOpeningId(null);
    }
  };

  const syncPayslips = async () => {
    setSyncing(true);
    try {
      await queuePayslipSync({
        force: true,
        lookback_months: 24,
        include_pending_availability: true,
      });
      await loadPayslips();
    } finally {
      setSyncing(false);
    }
  };

  const stats = useMemo(() => {
    const rows = state.data || [];
    const gross = rows.reduce((sum, row) => sum + Number(row.gross_amount || 0), 0);
    const net = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    return {
      count: rows.length,
      gross,
      net,
      averageNet: rows.length ? net / rows.length : 0,
      latest: rows[0],
    };
  }, [state.data]);

  const title = isAdminMode ? t("payslips.admin_title") : isStatsMode ? t("payslips.stats_title") : t("payslips.mine_title");
  const description = isAdminMode ? t("payslips.admin_description") : isStatsMode ? t("payslips.stats_description") : t("payslips.description");

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      {roleViewCtx?.isOverrideActive ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {t("payslips.role_view_override_warning")}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-sky-600">
              <BanknotesIcon className="h-5 w-5" />
              {t("payslips.kicker")}
            </div>
            <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
            <p className="mt-2 max-w-2xl text-sm text-gray-600">{description}</p>
          </div>

          {isAdminMode && state.canManage && (
            <button
              type="button"
              onClick={syncPayslips}
              disabled={syncing}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ArrowPathIcon className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? t("payslips.syncing") : t("payslips.sync_now")}
            </button>
          )}
        </div>
      </div>

      {isAdminMode && (
        <>
          {!state.isLoading && !state.canManage ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
              {t("payslips.admin_forbidden")}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm lg:grid-cols-[minmax(0,1fr)_320px]">
              <PeriodSelector
                periodType={period.periodType}
                from={period.from}
                to={period.to}
                allowedPeriodTypes={["month", "quarter", "year", "custom"]}
                onChange={(nextPeriod) => setPeriod(nextPeriod)}
              />
              <EmployeeSelector
                label={t("payslips.employee_filter")}
                selected={selectedEmployee}
                onChange={(event) => setSelectedEmployee(event.target.object?.id ? event.target.object : null)}
              />
            </div>
          )}
        </>
      )}

      {isStatsMode && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label={t("payslips.stats_count")} value={stats.count} />
          <StatCard label={t("payslips.stats_gross")} value={formatAmount(stats.gross)} />
          <StatCard label={t("payslips.stats_net")} value={formatAmount(stats.net)} />
          <StatCard label={t("payslips.stats_average_net")} value={formatAmount(stats.averageNet)} />
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {state.isLoading ? (
          <div className="p-6 text-sm text-gray-500">{t("payslips.loading")}</div>
        ) : state.error ? (
          <div className="p-6 text-sm text-red-600">{t("payslips.error")}</div>
        ) : isAdminMode && !state.canManage ? (
          <div className="p-6 text-sm text-gray-500">{t("payslips.admin_forbidden")}</div>
        ) : state.data.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">{t("payslips.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">{t("payslips.period")}</th>
                  {isAdminMode && <th className="px-4 py-3">{t("payslips.employee")}</th>}
                  <th className="px-4 py-3">{t("payslips.voucher_date")}</th>
                  <th className="px-4 py-3">{t("payslips.available_date")}</th>
                  <th className="px-4 py-3 text-right">{t("payslips.gross_amount")}</th>
                  <th className="px-4 py-3 text-right">{t("payslips.amount")}</th>
                  <th className="px-4 py-3 text-right">{t("payslips.actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {state.data.map((payslip) => (
                  <tr key={payslip.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">{payslip.period_label || "-"}</td>
                    {isAdminMode && <td className="whitespace-nowrap px-4 py-3 text-gray-700">{payslip.user?.displayTitle || payslip.user?.name || "-"}</td>}
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">{formatDate(payslip.voucher_date)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">{formatDate(payslip.available_date)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-gray-700">{formatAmount(payslip.gross_amount)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-gray-700">{formatAmount(payslip.amount)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openPayslip(payslip)}
                        disabled={openingId === payslip.id}
                        className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4" />
                        {openingId === payslip.id ? t("payslips.opening") : t("payslips.open")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="text-sm font-medium text-gray-500">{label}</div>
    <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
  </div>
);

export const MyPayslips = () => <PayslipList mode="mine" />;
export const PayslipStats = () => <PayslipList mode="stats" />;
export const AdminPayslips = () => <PayslipList mode="admin" />;
